import {
  Box,
  Stack,
  Link,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Colors } from "@src/gatsby-theme-material-ui-top-layout/colors-twentyfive";
import { motion } from "motion/react";
import {
  PitExample,
  RENDER_TYPE,
  SectionHeader,
  SectionHeaderHighlight,
  textStyles,
} from "@src/helpers/taxonomy";

import React, { FC, useEffect, useState } from "react";
import GlassContainer from "./GlassContainer";
import MarkdownWrapper from "../Markdown/MarkdownWrapper";
import { useWebsiteStore } from "@src/hooks/useWebsiteStore";

interface Props {
  example: PitExample;
}

function renderStep(example: PitExample, step: string, index: number) {
  if (example?.renderType === RENDER_TYPE.MARKDOWN) {
    return <MarkdownWrapper content={step} />;
  }

  if (example?.renderType === RENDER_TYPE.BLOCK) {
    return (
      <pre
        style={{
          border: "solid 1px rgb(134, 134, 134)",
          backgroundColor: `rgba(255, 255, 255, 0.07)`,
          padding: "15px",
          borderRadius: "15px 0px 15px 15px",
          whiteSpace: "pre-line",
        }}
      >
        <code>{step}</code>
      </pre>
    );
  }
  if (example?.renderType === RENDER_TYPE.TEXT) {
    return (
      <Typography
        sx={{
          ...textStyles,
        }}
      >
        {step}
      </Typography>
    );
  }

  if (example?.renderType === RENDER_TYPE.BASE64) {
    return (
      <pre>
        <code>{step}</code>
      </pre>
    );
  }

  return (
    <Typography
      sx={{
        ...textStyles,
        fontFamily: "Source Code Pro",
        fontSize: "18px",
      }}
      key={`step-${index}`}
    >
      {step}
    </Typography>
  );
}

const Example: FC<Props> = ({ example }) => {
  const currentExample = useWebsiteStore(
    (state) => state.currentTaxonomyExample,
  );
  const [open, setOpen] = useState(currentExample === example.id);
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up("lg"));

  const setCurrentTaxonomyExample = useWebsiteStore(
    (state) => state.actions.setCurrentTaxonomyExample,
  );

  const [steps, setSteps] = useState([]);

  useEffect(() => {
    if (example.multistep) {
      try {
        setSteps(JSON.parse(example?.example));
      } catch (e) {
        console.error("Could not render example: ", e);
      }
    }
  }, []);

  useEffect(() => {
    setOpen(currentExample === example.id);
  }, [currentExample]);

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          translate: "0px -20px",
        }}
        layout
        transition={{
          delay: 0.06,
        }}
        whileInView={{
          opacity: 1,
          translate: "0px 0px",
        }}
      >
        <Link
          role="button"
          onClick={() => {
            setCurrentTaxonomyExample(example.id);
          }}
          sx={{
            ":hover": {
              cursor: open ? "auto" : "pointer",
              opacity: isLarge ? 0.5 : 1,
            },
            textDecoration: "none",
          }}
        >
          <GlassContainer>
            <>
              <Box
                component={motion.div}
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 10,
                  borderRadius: "15px",
                  display: "flex",
                  width: "100%",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundImage: `linear-gradient(0deg,${Colors.InfoMain}54 10%, ${Colors.InfoMain}2B 100%)`,
                  textShadow: "0px 2px 15px rgba(0,0,0,0.58)",
                  pointerEvents: !open ? "all" : "none",
                }}
                initial={{
                  opacity: 0,
                }}
                whileHover={{
                  opacity: !open && isLarge ? 1 : 0,
                }}
              >
                Read more
              </Box>

              <Stack
                sx={{
                  gap: "20px",
                }}
              >
                <motion.div
                  initial={{
                    height: "120px",
                  }}
                  animate={{
                    height: open ? "auto" : "120px",
                  }}
                  style={{
                    position: "relative",
                    overflowY: "hidden",
                    zIndex: 0,
                    WebkitMaskImage: !open
                      ? "linear-gradient(to bottom, rgba(0,0,0,1) 90%, rgba(0,0,0,0) 100%)"
                      : "",
                  }}
                >
                  <Stack>
                    <Stack
                      sx={{
                        flexDirection: "row",
                        justifyContent: "space-between",
                      }}
                    >
                      <SectionHeaderHighlight>
                        {example.id}
                      </SectionHeaderHighlight>
                      <SectionHeader>EXAMPLE</SectionHeader>
                    </Stack>

                    <Stack
                      id="modal-modal-description"
                      sx={{
                        gap: "24px",
                      }}
                    >
                      {example?.example && example?.example?.trim() !== "" && (
                        <Stack
                          sx={{
                            gap: "0px",
                          }}
                        >
                          <SectionHeader
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {example?.exampleLabel ?? "Example"}
                          </SectionHeader>

                          <Stack
                            sx={{
                              gap: "24px",
                            }}
                          >
                            {example?.multistep === true && steps.length > 0 ? (
                              <>
                                {steps.map((step, index) => {
                                  return (
                                    <Stack
                                      key={index}
                                      sx={{
                                        gap: "16px",
                                      }}
                                    >
                                      <SectionHeader
                                        sx={{
                                          marginBottom: "0px",
                                        }}
                                      >
                                        Step {index + 1}
                                      </SectionHeader>

                                      {renderStep(example, step, index)}
                                    </Stack>
                                  );
                                })}
                              </>
                            ) : (
                              renderStep(example, example?.example, -1)
                            )}
                          </Stack>
                        </Stack>
                      )}

                      {example?.notes && example?.notes?.trim() !== "" && (
                        <Box>
                          <SectionHeader>Notes</SectionHeader>

                          <MarkdownWrapper content={example?.notes} />
                        </Box>
                      )}

                      {example?.source && example?.source?.trim() !== "" && (
                        <Box>
                          <SectionHeader>Source</SectionHeader>

                          {example?.source &&
                            example?.source?.trim() !== "" && (
                              <MarkdownWrapper content={example?.source} />
                            )}
                        </Box>
                      )}

                      {example?.categoriesWithNotes &&
                        example?.categoriesWithNotes?.length > 0 && (
                          <Box>
                            <SectionHeader>Mapped Categories</SectionHeader>

                            <ul>
                              {example?.categoriesWithNotes?.map(
                                (category: string, index: number) => {
                                  return (
                                    <li
                                      key={`example-category-${index}`}
                                      style={{
                                        ...textStyles,
                                      }}
                                    >
                                      {category}
                                    </li>
                                  );
                                },
                              )}
                            </ul>
                          </Box>
                        )}
                    </Stack>
                  </Stack>
                </motion.div>
              </Stack>
            </>
          </GlassContainer>
        </Link>
      </motion.div>
    </>
  );
};

export default Example;
